import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Eye, MapPin, Plus, RefreshCw, Video } from 'lucide-react'
import { Link } from 'react-router-dom'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { fmtNumber } from '@/shared/lib/format'
import { useLoad } from '@/features/administration/hooks'
import { forceEndFeed, listLiveCoverage } from './api'
import { LiveCoveragePlayer, fmtClock } from './players'
import { classificationLabel, isSensitive, placeOf } from './vocab'
import { explain } from '@/shared/api/problem'
import type { LiveFeedResponse } from '@/shared/api/types'

const POLL_MS = 8000

/**
 * What is on air right now.
 *
 * The board polls; the player does not. Once somebody opens a feed the poll stops
 * — swapping props under a playing `<video>` costs frames, and a person watching
 * one camera is not reading the board behind it.
 */
export function LiveCoveragePage() {
  const canRead = useCan('coverage:read')
  const canWatch = useCan('coverage:watch')
  const canAdmin = useCan('coverage:admin')
  const [watching, setWatching] = useState<LiveFeedResponse | null>(null)
  const [tick, setTick] = useState(0)

  const { data, error, loading } = useLoad(() => listLiveCoverage(), [tick])
  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    if (watching) return
    const t = setInterval(refresh, POLL_MS)
    return () => clearInterval(t)
  }, [watching, refresh])

  if (!canRead) return <Forbidden what="live coverage" />

  const feeds = data ?? []
  const viewers = feeds.reduce((a, f) => a + (f.liveViewers ?? 0), 0)
  const stale = feeds.filter((f) => f.liveTooLong)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Live Now"
        description="Cameras on the campaign trail. The picture goes device to device — it does not pass through Axis."
        right={
          <div className="flex items-center gap-1.5">
            <button type="button" className="erp-btn" onClick={refresh} aria-label="Refresh">
              <RefreshCw className={loading ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
            </button>
            <Link className="erp-btn erp-btn-primary" to="/coverage/events">
              <Plus className="h-3.5 w-3.5" /> Events
            </Link>
          </div>
        }
      />

      <KpiStrip>
        <MetricCard label="Cameras live" value={fmtNumber(feeds.length)} sub="on air now" />
        <MetricCard label="Watching" value={fmtNumber(viewers)} sub="across all feeds" />
        <MetricCard
          label="Running long"
          value={fmtNumber(stale.length)}
          sub={stale.length ? 'probably nobody stopped them' : 'none'}
        />
      </KpiStrip>

      <ErrorStrip message={error} />

      {loading && !data ? <LoadingBlock label="Checking for live feeds…" /> : feeds.length === 0 ? (
        <EmptyState
          title="Nothing is broadcasting right now"
          hint="Create an event, open a camera slot on it, and send the broadcast link to whoever is holding the phone. The feed appears here the moment they go live."
        />
      ) : (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
        >
          {feeds.map((f) => (
            <LiveCard
              key={f.feedId}
              feed={f}
              canWatch={canWatch}
              onOpen={() => setWatching(f)}
            />
          ))}
        </div>
      )}

      {/*
        A `title` on a DISABLED button is unreachable: it takes no focus and no
        reliable native tooltip, so the explanation is invisible to a keyboard and
        to a screen reader. Say it once, in the page, as text.
      */}
      {feeds.length > 0 && !canWatch && (
        <Note tone="info">
          You can see which cameras are on air. Opening one needs the watch
          permission, which you do not hold.
        </Note>
      )}

      {stale.length > 0 && (
        <Panel
          title="Running longer than expected"
          description="Still marked live well past a normal session. Usually a camera nobody switched off."
        >
          <ul className="space-y-1">
            {stale.map((f) => (
              <li key={f.feedId} className="flex items-center gap-2 text-erp-xs">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                <span className="truncate">{f.eventTitle} — {f.feedTitle}</span>
                <span className="shrink-0 text-muted-foreground">
                  since {f.startedAt ? new Date(f.startedAt).toLocaleString() : '—'}
                </span>
                {canAdmin && <ForceEndButton feed={f} onDone={refresh} />}
              </li>
            ))}
          </ul>
          <div className="mt-2">
            <Note tone="info">
              Reconciliation closes these on its own once the media server confirms nobody is
              publishing. Forcing one off air is for when you need it stopped now.
            </Note>
          </div>
        </Panel>
      )}

      {watching && (
        <Modal
          open
          onClose={() => { setWatching(null); refresh() }}
          title={watching.eventTitle}
          width="lg"
        >
          <div className="space-y-2">
            <div className="text-erp-xs text-muted-foreground">
              {watching.feedTitle} · {placeOf(watching)}
              {watching.broadcasterName ? ` · filmed by ${watching.broadcasterName}` : ''}
            </div>
            {canWatch ? (
              <LiveCoveragePlayer feedId={watching.feedId} className="aspect-video w-full" />
            ) : (
              <Note tone="info">
                You can see that this feed exists but you have not been granted the permission
                to watch it.
              </Note>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}

function LiveCard({
  feed, canWatch, onOpen,
}: { feed: LiveFeedResponse; canWatch: boolean; onOpen: () => void }) {
  const since = feed.startedAt ? Math.floor((Date.now() - Date.parse(feed.startedAt)) / 1000) : null
  return (
    <button
      type="button"
      className="erp-card overflow-hidden text-left transition hover:border-chocolate/50 disabled:opacity-60"
      onClick={onOpen}
      disabled={!canWatch}
    >
      <div className="relative aspect-video bg-black">
        <div className="grid h-full place-items-center">
          <Video className="h-7 w-7 text-white/30" />
        </div>
        <span className="absolute left-2 top-2 flex items-center gap-1.5 rounded-full bg-red-600 px-2 py-0.5 text-erp-xs font-semibold text-white">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> LIVE
        </span>
        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-erp-xs text-white">
          <Eye className="h-3 w-3" /> {fmtNumber(feed.liveViewers)}
        </span>
        {since != null && (
          <span className="absolute bottom-2 right-2 rounded bg-black/60 px-1.5 py-0.5 text-erp-xs tabular-nums text-white">
            {fmtClock(since)}
          </span>
        )}
      </div>
      <div className="space-y-0.5 p-2.5">
        <div className="flex items-center gap-1.5">
          <div className="truncate text-erp-sm font-semibold">{feed.eventTitle}</div>
          {isSensitive(feed.classificationCode) && (
            <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-erp-xs font-medium text-amber-700 dark:text-amber-400">
              {classificationLabel(feed.classificationCode)}
            </span>
          )}
        </div>
        <div className="truncate text-erp-xs text-muted-foreground">{feed.feedTitle}</div>
        <div className="flex items-center gap-1 truncate text-erp-xs text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0" /> {placeOf(feed)}
        </div>
      </div>
    </button>
  )
}

function ForceEndButton({ feed, onDone }: { feed: LiveFeedResponse; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <>
      <button
        type="button"
        className="erp-btn-ghost h-6 shrink-0 text-erp-xs text-chocolate"
        onClick={(e) => { e.stopPropagation(); setOpen(true) }}
      >
        Force off air
      </button>
      {open && (
        <Modal open onClose={() => setOpen(false)} title="Force this camera off air">
          <div className="space-y-3">
            <p className="text-erp-sm text-muted-foreground">
              {feed.eventTitle} — {feed.feedTitle}. The media server drops the publisher
              immediately. Whoever is holding the device is not told why.
            </p>
            <ErrorStrip message={error} />
            <label className="block space-y-1">
              <span className="text-erp-xs font-medium">Reason</span>
              <input
                className="erp-input w-full"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Recorded in the audit log"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="erp-btn" onClick={() => setOpen(false)}>Cancel</button>
              <button
                type="button"
                className="erp-btn erp-btn-primary"
                disabled={!reason.trim() || busy}
                onClick={async () => {
                  setBusy(true); setError(null)
                  try {
                    await forceEndFeed(feed.feedId, reason.trim())
                    setOpen(false)
                    onDone()
                  } catch (e) { setError(explain(e)) } finally { setBusy(false) }
                }}
              >
                Force off air
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
